import { AlertTriangle, AlertCircle, Lightbulb, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import type { Insight, InsightType } from '../../api/client';

interface InsightCardProps {
  insight: Insight;
  onDismiss: (id: string) => void;
  onResolve: (id: string) => void;
  onViewDetail: (insight: Insight) => void;
}

const categoryIcons = {
  critical: AlertTriangle,
  warning: AlertCircle,
  opportunity: Lightbulb,
  positive: CheckCircle2,
};

const categoryColors = {
  critical: 'text-red-500',
  warning: 'text-yellow-500',
  opportunity: 'text-blue-500',
  positive: 'text-green-500',
};

const typeLabels: Record<InsightType, string> = {
  page_performance: 'Page Performance',
  traffic_source: 'Traffic Source',
  ux_issue: 'UX Issue',
  form_optimization: 'Form Optimization',
  conversion_flow: 'Conversion Flow',
  error_impact: 'Error Impact',
  weekly_digest: 'Weekly Digest',
  device_browser: 'Device / Browser',
  landing_page_intelligence: 'Landing Page Intelligence',
  session_deep: 'Session Deep-Dive',
  pattern_detection: 'Pattern Detection',
};

function impactScoreColor(score: number): string {
  if (score >= 80) return 'bg-red-100 text-red-700 border-red-200';
  if (score >= 50) return 'bg-orange-100 text-orange-700 border-orange-200';
  if (score >= 20) return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-green-100 text-green-700 border-green-200';
}

export default function InsightCard({ insight, onDismiss, onResolve, onViewDetail }: InsightCardProps) {
  const CategoryIcon = categoryIcons[insight.category] || AlertCircle;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-gray-300">
      <div className="flex gap-4">
        {/* Impact Score Badge */}
        <div
          className={clsx(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-bold',
            impactScoreColor(insight.impactScore),
          )}
        >
          {insight.impactScore}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <CategoryIcon className={clsx('h-4 w-4 shrink-0', categoryColors[insight.category])} />
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {typeLabels[insight.type] || insight.type}
            </span>
          </div>

          <h3 className="text-sm font-semibold text-gray-900">{insight.title}</h3>
          <p className="mt-1 line-clamp-3 text-sm text-gray-600">{insight.description}</p>

          {/* Affected Pages */}
          {insight.affectedPages && insight.affectedPages.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {insight.affectedPages.slice(0, 3).map((page) => (
                <span
                  key={page}
                  className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                >
                  {page}
                </span>
              ))}
              {insight.affectedPages.length > 3 && (
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                  +{insight.affectedPages.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={() => onViewDetail(insight)}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              View Details
            </button>
            <button
              onClick={() => onDismiss(insight.id)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
            <button
              onClick={() => onResolve(insight.id)}
              className="text-sm text-green-600 hover:text-green-700"
            >
              Resolve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
